import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Gift, Sparkles, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { requestOnboardingSlot, releaseOnboardingSlot } from '../onboardingQueue';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const SEEN_PREFIX = 'rebate_recap_seen_';

/**
 * RebateRecapModal — celebratory pop-up shown ONCE per rebate txn.
 *
 * On mount (players only), hits `GET /user/last-rebate-recap`. If the
 * player has an unseen rebate from the last 8 days, an overlay fires with
 * a big number and a "Play it now" CTA. The txn id is written to
 * localStorage so re-mounts don't re-open it.
 *
 * Uses a plain fixed-position overlay instead of Radix Dialog to avoid
 * portal / stacking collisions with the first-load promo and coachmark
 * modals that fire on the same lobby render.
 */
const RebateRecapModal = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [rebate, setRebate] = useState(null);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (!user || user.role !== 'user') return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await axios.get(
                    `${API}/user/last-rebate-recap`,
                    { withCredentials: true },
                );
                if (cancelled || !data?.has_rebate) return;
                const seenKey = `${SEEN_PREFIX}${data.transaction_id}`;
                if (typeof window !== 'undefined' && localStorage.getItem(seenKey) === '1') return;
                setRebate(data);
                // Queue behind other onboarding modals so nothing stacks.
                requestOnboardingSlot('rebate-recap', 25, () => setOpen(true));
            } catch { /* silent */ }
        })();
        return () => { cancelled = true; };
    }, [user]);

    // Release our queue slot if we unmount (e.g. navigate off the lobby)
    // before being shown, so we never deadlock the onboarding queue.
    useEffect(() => () => releaseOnboardingSlot('rebate-recap'), []);

    const handleClose = () => {
        if (rebate?.transaction_id) {
            try { localStorage.setItem(`${SEEN_PREFIX}${rebate.transaction_id}`, '1'); } catch { /* ignore */ }
        }
        setOpen(false);
        releaseOnboardingSlot('rebate-recap');
    };

    // "Play It Now" — dismisses AND deep-links to the slots lobby so the
    // player lands straight where they can spend the fresh Free Play.
    const handlePlayNow = () => {
        if (rebate?.transaction_id) {
            try { localStorage.setItem(`${SEEN_PREFIX}${rebate.transaction_id}`, '1'); } catch { /* ignore */ }
        }
        setOpen(false);
        releaseOnboardingSlot('rebate-recap');
        navigate('/casino/slots');
    };

    if (!open || !rebate) return null;

    return (
        <div
            className="fixed inset-0 z-[9998] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            data-testid="rebate-recap-modal"
            onClick={handleClose}
            role="dialog"
            aria-modal="true"
            aria-label="Weekly rebate arrived"
        >
            <div
                className="w-full max-w-md rounded-2xl bg-gradient-to-br from-[#0a0a0a] via-[#0f0a1a] to-[#0a0a0a] border-2 border-[#FFD700] shadow-[0_0_60px_rgba(255,215,0,0.4)] p-6 text-center relative"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    type="button"
                    onClick={handleClose}
                    className="absolute top-3 right-3 text-white/50 hover:text-white transition"
                    aria-label="Close"
                    data-testid="rebate-recap-close-x"
                >
                    <X className="w-5 h-5" />
                </button>

                <div className="flex justify-center gap-2 mb-3 text-2xl" aria-hidden="true">
                    <Sparkles className="w-6 h-6 text-[#FFD700]" />
                    <span className="text-[#39FF14]">✨</span>
                    <Sparkles className="w-6 h-6 text-[#00FFFF]" />
                </div>

                <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#A0AAB2] mb-2">
                    Weekly Rebate Delivered
                </p>

                <div className="w-20 h-20 mx-auto rounded-full bg-[#FFD700]/15 border-2 border-[#FFD700] flex items-center justify-center mb-4 shadow-[0_0_30px_rgba(255,215,0,0.45)]">
                    <Gift className="w-10 h-10 text-[#FFD700]" />
                </div>

                <h2 className="text-3xl sm:text-4xl font-black text-[#FFD700] font-mono mb-1 tabular-nums">
                    +${rebate.amount.toFixed(2)}
                </h2>
                <p className="text-white font-bold text-lg mb-1" data-testid="rebate-recap-in-balance">is in your balance as Free Play</p>
                <p className="text-[#A0AAB2] text-sm mb-6 max-w-xs mx-auto leading-relaxed">
                    Every 6% cashout fee you paid last week came back as{' '}
                    <span className="text-[#39FF14] font-semibold">Free Play</span> — play it
                    through once and any winnings are cashable.
                </p>

                <Button
                    onClick={handlePlayNow}
                    className="w-full bg-[#FFD700] hover:bg-[#FFC700] text-black font-black text-base py-6 shadow-lg"
                    data-testid="rebate-recap-play-btn"
                >
                    Play Now →
                </Button>
                <button
                    onClick={handleClose}
                    type="button"
                    className="text-[11px] text-[#A0AAB2] mt-3 hover:text-white transition"
                    data-testid="rebate-recap-dismiss-btn"
                >
                    Maybe later
                </button>
            </div>
        </div>
    );
};

export default RebateRecapModal;
